import { useState, useEffect } from "react";
import {
  Popover,
  Button,
  DatePicker,
  Box,
  Scrollable,
  OptionList,
  useBreakpoints,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";

const DateFilter = ({ onFilterChange, url = "/dashboard/staff" }) => {
  const navigateTo = useNavigate();
  const { lgUp } = useBreakpoints();
  const shouldShowMultiMonth = lgUp;
  const params = new URLSearchParams(window.location.search);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const ranges = [
    { title: "Today", alias: "today", period: { since: today, until: today } },
    {
      title: "Yesterday",
      alias: "yesterday",
      period: { since: yesterday, until: yesterday },
    },
    {
      title: "Last 7 days",
      alias: "last7days",
      period: {
        since: new Date(new Date().setDate(today.getDate() - 7)),
        until: yesterday,
      },
    },
    {
      title: "Current Month",
      alias: "currentMonth",
      period: {
        since: new Date(today.getFullYear(), today.getMonth(), 1),
        until: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      },
    },
    {
      title: "Last Month",
      alias: "lastMonth",
      period: {
        since: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        until: new Date(today.getFullYear(), today.getMonth(), 0),
      },
    },
    {
      title: "Last 6 Months",
      alias: "last6Months",
      period: {
        since: new Date(today.getFullYear(), today.getMonth() - 6, 1),
        until: new Date(today.getFullYear(), today.getMonth(), 0),
      },
    },
    {
      title: "This Year",
      alias: "thisYear",
      period: {
        since: new Date(today.getFullYear(), 0, 1),
        until: new Date(today.getFullYear(), 11, 31),
      },
    },
    {
      title: "Last Year",
      alias: "lastYear",
      period: {
        since: new Date(today.getFullYear() - 1, 0, 1),
        until: new Date(today.getFullYear() - 1, 11, 31),
      },
    },
  ];

  const [popoverActive, setPopoverActive] = useState(false);
  const [activeDateRange, setActiveDateRange] = useState(ranges[0]);
  const [selectedDates, setSelectedDates] = useState(null);

  useEffect(() => {
    navigateTo(
      `${url}?bookingfrom=${params.get("bookingfrom") || ""}&bookingto=${
        params.get("bookingto") || ""
      }`
    );
  }, []);

  useEffect(() => {
    params.set("bookingfrom", activeDateRange?.period?.since?.toDateString());
    params.set("bookingto", activeDateRange?.period?.until?.toDateString());
    navigateTo({ search: params.toString() });
    onFilterChange({
      dates: {
        start: activeDateRange?.period?.since,
        end: activeDateRange?.period?.until,
      },
    });
  }, [activeDateRange]);

  const handleDateChange = (dt) => {
    params.set("bookingfrom", dt.start.toDateString());
    params.set("bookingto", dt.end.toDateString());
    const newDateRange = ranges.find((range) => {
      return (
        range.period.since.valueOf() === dt?.start?.valueOf() &&
        range.period.until.valueOf() === dt?.end?.valueOf()
      );
    }) || {
      alias: "custom",
      title: "custom",
      period: {
        since: dt?.start,
        until: dt?.end,
      },
    };
    setActiveDateRange(newDateRange);
    setSelectedDates(dt);
    navigateTo({ search: params.toString() });
    onFilterChange({ dates: dt });
  };
  const handleMonthChange = (month, year) => {
    setActiveDateRange((prevState) => {
      const newPeriod = {
        since: new Date(year, month, 1),
        until: new Date(year, month + 1, 0),
      };
      return {
        ...prevState,
        period: newPeriod,
      };
    });
  };

  return (
    <Popover
      active={popoverActive}
      preferredAlignment="left"
      preferredPosition="below"
      activator={
        <Button onClick={() => setPopoverActive(!popoverActive)}>
          {activeDateRange.title}
        </Button>
      }
      onClose={() => setPopoverActive(false)}
    >
      <Popover.Pane fixed>
        <Scrollable style={{ maxHeight: "200px" }}>
          <OptionList
            options={ranges.map(({ alias, title }) => ({
              value: alias,
              label: title,
            }))}
            selected={activeDateRange.alias}
            onChange={(value) => {
              setActiveDateRange(ranges.find((r) => r.alias === value[0]));
            }}
          />
        </Scrollable>
      </Popover.Pane>
      <Popover.Pane>
        <Box padding="400">
          <DatePicker
            month={activeDateRange.period.since.getMonth()}
            year={activeDateRange.period.since.getFullYear()}
            selected={selectedDates}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            allowRange
            multiMonth={shouldShowMultiMonth}
          />
        </Box>
      </Popover.Pane>
    </Popover>
  );
};

export default DateFilter;
